import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { FaBars, FaUser, FaSignOutAlt, FaHome, FaBook, FaWallet, FaSearch, FaTimes } from 'react-icons/fa';
import SidebarLink from './SidebarLink';
import API_BASE_URL from '../../config/api';

const DashboardLayout = ({ children, title = "Dashboard" }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [userData, setUserData] = useState({ fullName: 'Loading...', phone: '...' });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const loggedInUser = JSON.parse(localStorage.getItem('user'));
        if (loggedInUser && loggedInUser.id) {
          const response = await fetch(`${API_BASE_URL}/get_profile.php?id=${loggedInUser.id}`);
          const result = await response.json();
          if (result.status === 'success') {
            setUserData(result.data);
          }
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };
    fetchUserData();
  }, []);

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = '/login';
  };

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { icon: <FaHome />, label: "Home", to: "/student/dashboard" },
    { icon: <FaBook />, label: "My Courses", to: "/student/courses" },
    { icon: <FaSearch />, label: "All Classes", to: "/student/all-classes" },
    { icon: <FaWallet />, label: "Payments", to: "/student/payments" },
    { icon: <FaUser />, label: "Profile", to: "/student/profile" },
  ];

  return (
    <div className="min-h-screen bg-[#f0f2f5] font-sans text-slate-800">

      {/* Top Header */}
      <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 sticky top-0 z-30 shadow-sm">
        <div className="flex items-center gap-3">
          {/* Mobile hamburger */}
          <button
            className="lg:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
            onClick={() => setMobileMenuOpen(true)}
          >
            <FaBars className="text-slate-600 text-lg" />
          </button>
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 bg-orange-500 rounded-full flex items-center justify-center text-white text-lg font-bold border-2 border-white shadow-sm">K</div>
            <div className="flex flex-col leading-none">
              <span className="text-orange-500 font-bold text-sm leading-tight">Kasun</span>
              <span className="text-orange-500 font-bold text-sm leading-tight">Walakumbura</span>
            </div>
          </Link>
          <span className="hidden sm:block font-bold text-xs tracking-tight text-slate-400 border-l border-slate-200 pl-3">{title}</span>
        </div>

        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden border border-slate-100">
            {userData.profileImage ? (
              <img
                src={userData.profileImage.startsWith('http') ? userData.profileImage : `${API_BASE_URL.replace(/\/api$/, '')}/${userData.profileImage}`}
                alt="Profile"
                className="w-full h-full object-cover"
              />
            ) : (
              <FaUser className="text-slate-400 text-sm" />
            )}
          </div>
          <span className="text-xs font-bold text-slate-600 hidden sm:block max-w-[120px] truncate">{userData.fullName}</span>
        </div>
      </header>

      {/* Mobile Slide-out Menu Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-72 bg-white shadow-2xl flex flex-col overflow-y-auto">
            {/* Mobile Menu Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 bg-orange-500 rounded-full flex items-center justify-center text-white text-lg font-bold">K</div>
                <div>
                  <p className="font-bold text-slate-800 text-sm leading-tight">Kasun Walakumbura</p>
                  <p className="text-[10px] text-slate-400 font-bold">Student Portal</p>
                </div>
              </div>
              <button onClick={() => setMobileMenuOpen(false)} className="p-2 rounded-lg hover:bg-slate-100 transition-colors">
                <FaTimes className="text-slate-400 text-lg" />
              </button>
            </div>

            {/* User Card */}
            <div className="p-5 border-b border-slate-100">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center border-2 border-slate-50 overflow-hidden shadow-inner flex-shrink-0">
                  {userData.profileImage ? (
                    <img
                      src={userData.profileImage.startsWith('http') ? userData.profileImage : `${API_BASE_URL.replace(/\/api$/, '')}/${userData.profileImage}`}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <FaUser className="text-slate-300 text-2xl" />
                  )}
                </div>
                <div>
                  <p className="font-bold text-sm text-slate-800">{userData.fullName}</p>
                  <p className="text-[10px] text-slate-400 font-bold">{userData.phone}</p>
                </div>
              </div>
            </div>

            {/* Mobile Nav Links */}
            <nav className="flex-grow p-4 space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl font-bold text-sm transition-all ${
                    isActive(link.to)
                      ? 'bg-orange-500 text-white shadow-lg shadow-orange-100'
                      : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800'
                  }`}
                >
                  <span className="text-base">{link.icon}</span>
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Mobile Logout */}
            <div className="p-4 border-t border-slate-100">
              <button
                onClick={handleLogout}
                className="flex items-center gap-3 w-full px-4 py-3 text-sm font-bold text-red-500 hover:bg-red-50 rounded-xl transition-colors"
              >
                <FaSignOutAlt /> Logout
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex">
        {/* Desktop Sidebar */}
        <aside className="w-72 p-4 hidden lg:block flex-shrink-0">
          <div className="space-y-4 sticky top-20">

            {/* User Card */}
            <div className="bg-white rounded-lg p-6 flex flex-col items-center shadow-sm border border-slate-100">
              <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center mb-4 border-2 border-slate-50 overflow-hidden shadow-inner">
                {userData.profileImage ? (
                  <img
                    src={userData.profileImage.startsWith('http') ? userData.profileImage : `${API_BASE_URL.replace(/\/api$/, '')}/${userData.profileImage}`}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <FaUser className="text-slate-300 text-3xl" />
                )}
              </div>
              <h3 className="font-bold text-sm text-slate-700 text-center">{userData.fullName}</h3>
              <p className="text-[10px] text-slate-400 mt-1 font-bold">Mobile: {userData.phone}</p>
            </div>

            {/* Menu Card */}
            <div className="bg-white rounded-lg shadow-sm border border-slate-100 overflow-hidden">
              <nav className="flex flex-col">
                <SidebarLink icon={<FaHome />} label="Home" to="/student/dashboard" active={isActive('/student/dashboard')} />
                <SidebarLink icon={<FaBook />} label="My Courses" to="/student/courses" active={isActive('/student/courses')} />
                <SidebarLink icon={<FaSearch />} label="All Classes" to="/student/all-classes" active={isActive('/student/all-classes')} />
                <SidebarLink icon={<FaWallet />} label="Payments" to="/student/payments" active={isActive('/student/payments')} />
                <SidebarLink icon={<FaUser />} label="Profile" to="/student/profile" active={isActive('/student/profile')} />
              </nav>
            </div>

            {/* Logout Card */}
            <div className="bg-white rounded-lg shadow-sm border border-slate-100 p-2">
              <button onClick={handleLogout} className="flex items-center gap-3 w-full px-4 py-3 text-xs font-bold text-slate-500 hover:text-red-600 transition-colors">
                <FaSignOutAlt />
                Logout
              </button>
            </div>

            <div className="text-[10px] text-center text-slate-400 font-bold mt-4 uppercase tracking-widest">
              Copyright© 2026 Kasun Walakumbura
            </div>
          </div>
        </aside>

        {/* Main Content Area */}
        <main className="flex-grow p-3 lg:p-6 min-h-[calc(100vh-64px)] pb-24 lg:pb-6">
          <div className="bg-white rounded-lg shadow-sm border border-slate-100 min-h-full p-4 md:p-8">
            {children}
          </div>
        </main>
      </div>

      {/* Mobile Bottom Navigation Bar */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-slate-200 shadow-[0_-4px_20px_rgb(0,0,0,0.06)]">
        <div className="flex items-center justify-around">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`flex flex-col items-center gap-1 py-2 px-3 flex-1 transition-colors ${
                isActive(link.to) ? 'text-orange-500' : 'text-slate-400'
              }`}
            >
              <span className={`text-lg transition-transform ${isActive(link.to) ? 'scale-110' : ''}`}>{link.icon}</span>
              <span className="text-[9px] font-black uppercase tracking-wider leading-none">{link.label}</span>
              {isActive(link.to) && <span className="absolute top-0 w-6 h-0.5 bg-orange-500 rounded-full" />}
            </Link>
          ))}
        </div>
      </nav>
    </div>
  );
};

export default DashboardLayout;
